<template>
  <div class="sessions-section">
    <!-- Section Header -->
    <div class="sessions-header">
      <div class="sessions-header__left">
        <div class="section-indicator"></div>
        <div class="section-header-content">
          <h3 class="section-title">Active Sessions</h3>
          <p class="section-description">Manage devices where you're signed in</p>
        </div>
      </div>
      <v-btn
        v-if="sessions.length > 1"
        color="error"
        text
        small
        :loading="revokingAll"
        @click="confirmRevokeAll"
        class="revoke-all-btn">
        <v-icon left small>mdi-logout-variant</v-icon>
        Sign out all devices
      </v-btn>
    </div>

    <!-- Sessions List -->
    <div class="sessions-body">
      <!-- Loading State -->
      <div v-if="loading" class="sessions-loading">
        <v-skeleton-loader
          v-for="i in 3"
          :key="i"
          type="list-item-avatar-two-line"
          class="session-skeleton" />
      </div>

      <!-- Empty State -->
      <div v-else-if="sessions.length === 0" class="sessions-empty">
        <div class="empty-icon">
          <v-icon size="48" color="grey lighten-1">mdi-devices</v-icon>
        </div>
        <p class="empty-text">No active sessions found</p>
        <v-btn small text color="primary" @click="fetchSessions">
          <v-icon left small>mdi-refresh</v-icon>
          Refresh
        </v-btn>
      </div>

      <!-- Sessions List -->
      <transition-group
        v-else
        name="session-list"
        tag="div"
        class="sessions-list">
        <div
          v-for="(session, index) in sessions"
          :key="session.id"
          class="session-card"
          :class="{
            'session-card--current': session.isCurrent,
            'session-card--revoking': revokingSession === session.id
          }"
          :style="{ '--index': index }">
          <!-- Device Icon -->
          <div class="session-device" :class="`session-device--${getDeviceType(session)}`">
            <v-icon :color="session.isCurrent ? 'primary' : 'grey'" size="24">
              {{ getDeviceIcon(session) }}
            </v-icon>
            <div v-if="session.isCurrent" class="current-indicator">
              <span class="pulse"></span>
            </div>
          </div>

          <!-- Session Info -->
          <div class="session-info">
            <div class="session-info__header">
              <span class="session-name">{{ getDeviceName(session) }}</span>
              <v-chip
                v-if="session.isCurrent"
                x-small
                color="primary"
                text-color="white"
                class="current-chip">
                This device
              </v-chip>
            </div>
            <!-- Location Info -->
            <div class="session-location">
              <v-icon x-small color="grey">mdi-map-marker-outline</v-icon>
              <span class="location-text">{{ formatLocation(session) }}</span>
              <v-tooltip v-if="session.timezone" bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-chip
                    v-bind="attrs"
                    v-on="on"
                    x-small
                    outlined
                    class="timezone-chip">
                    <v-icon x-small left>mdi-earth</v-icon>
                    {{ formatTimezoneShort(session.timezone) }}
                  </v-chip>
                </template>
                <span>{{ session.timezone }} • Local time: {{ getLocalTime(session.timezone) }}</span>
              </v-tooltip>
            </div>
            <!-- Session Meta -->
            <div class="session-meta">
              <span class="meta-item">
                <v-icon x-small color="grey">mdi-ip-network-outline</v-icon>
                {{ session.ipAddress || 'Unknown IP' }}
              </span>
              <span class="meta-divider"></span>
              <span class="meta-item">
                <v-icon x-small color="grey">mdi-clock-outline</v-icon>
                {{ formatDate(session.createDate) }}
              </span>
            </div>
            <div class="session-expiry">
              <v-icon x-small :color="isExpiringSoon(session) ? 'warning' : 'grey'">
                mdi-timer-sand
              </v-icon>
              <span :class="{ 'warning--text': isExpiringSoon(session) }">
                Expires {{ formatRelativeDate(session.expiresAt) }}
              </span>
            </div>
          </div>

          <!-- Actions -->
          <div class="session-actions">
            <v-btn
              v-if="!session.isCurrent"
              icon
              small
              :loading="revokingSession === session.id"
              @click="confirmRevoke(session)"
              class="revoke-btn">
              <v-icon small color="error">mdi-close-circle-outline</v-icon>
            </v-btn>
            <v-tooltip v-else bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  v-bind="attrs"
                  v-on="on"
                  small
                  color="success">
                  mdi-check-circle
                </v-icon>
              </template>
              <span>Current session</span>
            </v-tooltip>
          </div>
        </div>
      </transition-group>

      <!-- Refresh Button -->
      <div v-if="sessions.length > 0" class="sessions-footer">
        <v-btn
          text
          small
          color="primary"
          :loading="loading"
          @click="fetchSessions">
          <v-icon left small>mdi-refresh</v-icon>
          Refresh sessions
        </v-btn>
      </div>
    </div>

    <!-- Revoke Confirmation Dialog -->
    <v-dialog v-model="revokeDialog" max-width="400" persistent>
      <v-card class="revoke-dialog">
        <v-card-title class="revoke-dialog__title">
          <v-icon color="warning" class="mr-2">mdi-alert-circle-outline</v-icon>
          Revoke Session
        </v-card-title>
        <v-card-text class="revoke-dialog__text">
          <p v-if="sessionToRevoke">
            Are you sure you want to sign out from
            <strong>{{ getDeviceName(sessionToRevoke) }}</strong>?
          </p>
          <p class="text-caption grey--text">
            This device will need to sign in again to access the account.
          </p>
        </v-card-text>
        <v-card-actions class="revoke-dialog__actions">
          <v-spacer></v-spacer>
          <v-btn text @click="revokeDialog = false">Cancel</v-btn>
          <v-btn
            color="error"
            depressed
            :loading="revokingSession !== null"
            @click="revokeSession">
            Sign Out
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Revoke All Confirmation Dialog -->
    <v-dialog v-model="revokeAllDialog" max-width="450" persistent>
      <v-card class="revoke-dialog revoke-dialog--danger">
        <v-card-title class="revoke-dialog__title">
          <v-icon color="error" class="mr-2">mdi-logout-variant</v-icon>
          Sign Out All Devices
        </v-card-title>
        <v-card-text class="revoke-dialog__text">
          <p>
            This will sign you out from <strong>all devices</strong> including this one.
            You will need to sign in again.
          </p>
          <div class="affected-devices">
            <span class="affected-count">{{ sessions.length }} active session(s)</span>
          </div>
        </v-card-text>
        <v-card-actions class="revoke-dialog__actions">
          <v-spacer></v-spacer>
          <v-btn text @click="revokeAllDialog = false">Cancel</v-btn>
          <v-btn
            color="error"
            depressed
            :loading="revokingAll"
            @click="revokeAllSessions">
            Sign Out All
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import authService from '@Account/service/auth.service';
import { $handleError } from '@/utils';

export default {
  name: 'ActiveSessions',

  data() {
    return {
      sessions: [],
      loading: false,
      revokeDialog: false,
      revokeAllDialog: false,
      sessionToRevoke: null,
      revokingSession: null,
      revokingAll: false
    };
  },

  mounted() {
    this.fetchSessions();
  },

  methods: {
    async fetchSessions() {
      this.loading = true;
      try {
        const response = await authService.getSessions();
        this.sessions = response.data || [];
      } catch (err) {
        $handleError(err);
        this.sessions = [];
      } finally {
        this.loading = false;
      }
    },

    getDeviceType(session) {
      const clientType = (session.clientType || '').toLowerCase();
      if (clientType === 'mobile') return 'mobile';
      if (clientType === 'desktop') return 'desktop';
      return 'web';
    },

    getDeviceIcon(session) {
      const type = this.getDeviceType(session);
      const icons = {
        mobile: 'mdi-cellphone',
        desktop: 'mdi-desktop-tower-monitor',
        web: 'mdi-web'
      };
      return icons[type] || 'mdi-devices';
    },

    getDeviceName(session) {
      if (session.deviceName) return session.deviceName;

      const type = this.getDeviceType(session);
      const names = {
        mobile: 'Mobile Device',
        desktop: 'Desktop App',
        web: 'Web Browser'
      };
      return names[type] || 'Unknown Device';
    },

    formatDate(date) {
      if (!date) return 'Unknown';
      const d = new Date(date);
      // Display in user's local timezone using their locale preferences
      return d.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    },

    formatRelativeDate(date) {
      if (!date) return 'Unknown';
      const now = new Date();
      const expiry = new Date(date);
      const diffMs = expiry - now;
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffMins = Math.floor(diffMs / (1000 * 60));

      if (diffMs < 0) return 'expired';
      if (diffDays > 0) return `in ${diffDays} day${diffDays > 1 ? 's' : ''}`;
      if (diffHours > 0) return `in ${diffHours} hour${diffHours > 1 ? 's' : ''}`;
      if (diffMins > 0) return `in ${diffMins} min${diffMins > 1 ? 's' : ''}`;
      return 'soon';
    },

    isExpiringSoon(session) {
      if (!session.expiresAt) return false;
      const now = new Date();
      const expiry = new Date(session.expiresAt);
      const diffHours = (expiry - now) / (1000 * 60 * 60);
      return diffHours < 24;
    },

    formatLocation(session) {
      const parts = [];
      if (session.city) parts.push(session.city);
      if (session.region) parts.push(session.region);
      if (session.country) parts.push(session.country);

      if (parts.length === 0) return 'Unknown location';
      if (parts.length === 1) return parts[0];

      // Format: "City, Region, Country" or "City, Country" if region matches city
      if (parts.length === 3 && parts[0] === parts[1]) {
        return `${parts[0]}, ${parts[2]}`;
      }
      return parts.join(', ');
    },

    formatTimezoneShort(timezone) {
      if (!timezone) return '';
      // Extract the last part of timezone (e.g., "America/Los_Angeles" -> "Los Angeles")
      const parts = timezone.split('/');
      const location = parts[parts.length - 1].replace(/_/g, ' ');
      return location;
    },

    getLocalTime(timezone) {
      if (!timezone) return '';
      try {
        return new Date().toLocaleTimeString('en-US', {
          timeZone: timezone,
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        });
      } catch {
        return '';
      }
    },

    confirmRevoke(session) {
      this.sessionToRevoke = session;
      this.revokeDialog = true;
    },

    confirmRevokeAll() {
      this.revokeAllDialog = true;
    },

    async revokeSession() {
      if (!this.sessionToRevoke) return;

      this.revokingSession = this.sessionToRevoke.id;
      try {
        await authService.revokeSession(this.sessionToRevoke.id);
        // Remove session from local list
        this.sessions = this.sessions.filter(s => s.id !== this.sessionToRevoke.id);
        this.$dialog.notify.success('Session revoked successfully');
        this.revokeDialog = false;
        this.sessionToRevoke = null;
      } catch (err) {
        $handleError(err);
      } finally {
        this.revokingSession = null;
      }
    },

    async revokeAllSessions() {
      this.revokingAll = true;
      try {
        await this.$store.dispatch('ACCOUNT/LOGOUT_ALL');
        // Will redirect after logout
      } catch (err) {
        this.revokingAll = false;
        this.revokeAllDialog = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.sessions-section {
  background: var(--v-bg2-base);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08), 0 4px 12px rgba(0,0,0,0.04);
}

// Header
.sessions-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid rgba(0,0,0,0.06);
  flex-wrap: wrap;

  .theme--dark & {
    border-bottom-color: rgba(255,255,255,0.06);
  }

  &__left {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
  }
}

.section-indicator {
  width: 4px;
  align-self: stretch;
  min-height: 40px;
  background: var(--v-primary-base);
  border-radius: 2px;
  flex-shrink: 0;
}

.section-header-content {
  flex: 1;
}

.section-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0;
  color: var(--v-text-base);
}

.section-description {
  font-size: 0.85rem;
  color: var(--v-text-secondary);
  margin: 0.25rem 0 0;
  opacity: 0.7;
}

.revoke-all-btn {
  text-transform: none;
  font-weight: 500;
  letter-spacing: 0;
}

// Body
.sessions-body {
  padding: 1rem 1.5rem 1.5rem;
  max-height: 400px;
  overflow-y: auto;

  // Custom scrollbar
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.15);
    border-radius: 3px;

    &:hover {
      background: rgba(0, 0, 0, 0.25);
    }
  }

  .theme--dark &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.15);

    &:hover {
      background: rgba(255, 255, 255, 0.25);
    }
  }
}

// Loading State
.sessions-loading {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.session-skeleton {
  border-radius: 10px;
  overflow: hidden;
}

// Empty State
.sessions-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  text-align: center;
}

.empty-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(0,0,0,0.04);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;

  .theme--dark & {
    background: rgba(255,255,255,0.04);
  }
}

.empty-text {
  color: var(--v-text-secondary);
  margin: 0 0 1rem;
  font-size: 0.95rem;
}

// Sessions List
.sessions-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

// Session Card
.session-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
  background: var(--v-bg1-base);
  border-radius: 10px;
  border: 1px solid transparent;
  transition: all 0.25s ease;
  animation: slideIn 0.4s ease forwards;
  animation-delay: calc(var(--index) * 0.05s);
  opacity: 0;
  transform: translateY(8px);

  &:hover {
    border-color: rgba(0,0,0,0.08);
    box-shadow: 0 2px 8px rgba(0,0,0,0.06);

    .theme--dark & {
      border-color: rgba(255,255,255,0.08);
    }
  }

  &--current {
    border-color: var(--v-primary-lighten3);
    background: linear-gradient(135deg, rgba(3,98,76,0.05) 0%, transparent 100%);

    .theme--dark & {
      border-color: rgba(3,98,76,0.3);
      background: linear-gradient(135deg, rgba(3,98,76,0.1) 0%, transparent 100%);
    }
  }

  &--revoking {
    opacity: 0.5;
    pointer-events: none;
  }
}

@keyframes slideIn {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// Device Icon
.session-device {
  position: relative;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: rgba(0,0,0,0.04);
  transition: transform 0.2s ease;

  .theme--dark & {
    background: rgba(255,255,255,0.06);
  }

  &--web {
    background: linear-gradient(135deg, rgba(66,165,245,0.12) 0%, rgba(66,165,245,0.04) 100%);
  }

  &--desktop {
    background: linear-gradient(135deg, rgba(102,187,106,0.12) 0%, rgba(102,187,106,0.04) 100%);
  }

  &--mobile {
    background: linear-gradient(135deg, rgba(171,71,188,0.12) 0%, rgba(171,71,188,0.04) 100%);
  }
}

.current-indicator {
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 14px;
  height: 14px;
  background: var(--v-success-base);
  border-radius: 50%;
  border: 2px solid var(--v-bg1-base);
  display: flex;
  align-items: center;
  justify-content: center;

  .pulse {
    width: 6px;
    height: 6px;
    background: white;
    border-radius: 50%;
    animation: pulse 2s infinite;
  }
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.3);
    opacity: 0.7;
  }
}

// Session Info
.session-info {
  flex: 1;
  min-width: 0;

  &__header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.25rem;
  }
}

.session-name {
  font-weight: 600;
  font-size: 0.95rem;
  color: var(--v-text-base);
}

.current-chip {
  height: 18px !important;
  font-size: 0.65rem !important;
  font-weight: 600;
  letter-spacing: 0.02em;
}

// Location Info
.session-location {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.85rem;
  color: var(--v-text-base);
  margin-bottom: 0.35rem;
  flex-wrap: wrap;
}

.location-text {
  font-weight: 500;
}

.timezone-chip {
  height: 18px !important;
  font-size: 0.65rem !important;
  font-weight: 500;
  letter-spacing: 0;
  margin-left: 0.25rem;
  border-color: rgba(0, 0, 0, 0.12) !important;
  background: rgba(0, 0, 0, 0.02) !important;

  .theme--dark & {
    border-color: rgba(255, 255, 255, 0.15) !important;
    background: rgba(255, 255, 255, 0.04) !important;
  }

  .v-icon {
    font-size: 10px !important;
  }
}

.session-meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  color: var(--v-text-secondary);
  opacity: 0.75;
  margin-bottom: 0.25rem;
  flex-wrap: wrap;
}

.meta-item {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

.meta-divider {
  width: 3px;
  height: 3px;
  background: currentColor;
  border-radius: 50%;
  opacity: 0.4;
}

.session-expiry {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.75rem;
  color: var(--v-text-secondary);
  opacity: 0.6;
}

// Actions
.session-actions {
  flex-shrink: 0;
}

.revoke-btn {
  opacity: 0.6;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
  }
}

// Footer
.sessions-footer {
  display: flex;
  justify-content: center;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(0,0,0,0.04);

  .theme--dark & {
    border-top-color: rgba(255,255,255,0.04);
  }
}

// List Transitions
.session-list-enter-active,
.session-list-leave-active {
  transition: all 0.3s ease;
}

.session-list-enter-from,
.session-list-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}

.session-list-move {
  transition: transform 0.3s ease;
}

// Dialog Styles
.revoke-dialog {
  border-radius: 12px !important;
  overflow: hidden;

  &__title {
    font-size: 1.1rem !important;
    font-weight: 600;
    padding: 1.25rem 1.5rem !important;
    border-bottom: 1px solid rgba(0,0,0,0.06);

    .theme--dark & {
      border-bottom-color: rgba(255,255,255,0.06);
    }
  }

  &__text {
    padding: 1.5rem !important;

    p {
      margin: 0 0 0.5rem;
      line-height: 1.6;
    }
  }

  &__actions {
    padding: 1rem 1.5rem !important;
    border-top: 1px solid rgba(0,0,0,0.06);

    .theme--dark & {
      border-top-color: rgba(255,255,255,0.06);
    }
  }

  &--danger {
    .revoke-dialog__title {
      background: linear-gradient(135deg, rgba(244,67,54,0.08) 0%, transparent 100%);
    }
  }
}

.affected-devices {
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  background: rgba(244,67,54,0.06);
  border-radius: 8px;
  border-left: 3px solid var(--v-error-base);
}

.affected-count {
  font-weight: 600;
  font-size: 0.9rem;
  color: var(--v-error-base);
}

// Dark Mode
.theme--dark {
  .session-card {
    background: var(--v-bg3-base);

    &:hover {
      box-shadow: 0 2px 8px rgba(0,0,0,0.2);
    }
  }

  .sessions-section {
    box-shadow: 0 1px 3px rgba(0,0,0,0.2), 0 4px 12px rgba(0,0,0,0.15);
  }
}

// Responsive
@media (max-width: 600px) {
  .sessions-header {
    flex-direction: column;
    align-items: stretch;

    &__left {
      margin-bottom: 0.5rem;
    }
  }

  .session-card {
    flex-wrap: wrap;
    padding: 1rem;
  }

  .session-info {
    flex: 1 1 calc(100% - 64px);
    order: 1;
  }

  .session-device {
    order: 0;
  }

  .session-actions {
    order: 2;
    width: 100%;
    display: flex;
    justify-content: flex-end;
    margin-top: 0.5rem;
    padding-top: 0.5rem;
    border-top: 1px dashed rgba(0,0,0,0.06);

    .theme--dark & {
      border-top-color: rgba(255,255,255,0.06);
    }
  }
}
</style>
